/**
 * Global host for XP celebrations — mounted once in `app/_layout.tsx` (next to
 * `<GlobalSnackbar/>`). Renders whichever celebration the celebrationStore has
 * active; `key` remounts the component per celebration so each plays its full
 * timeline (including sound) from the start.
 *
 * A blocking overlay (e.g. the challenge SuccessModal) covers this host, so a
 * celebration waits it out — the store keeps it active, and it plays in full once the
 * overlay closes.
 */

import React from 'react';
import { useIsBlockedByOverlay } from '../../../shared/stores/overlayStore';
import { useCelebrationStore } from '../../stores/celebrationStore';
import { getRank } from '../../helpers/rankRegistry.helper';
import { useXpCelebrations } from '../../hooks/useXpCelebrations';
import { useCoinRankCelebrations } from '../../hooks/useCoinRankCelebrations';
import { LevelUpCelebration } from '../LevelUpCelebration';
import { RankUpCelebration } from '../RankUpCelebration';

export const CelebrationHost: React.FC = () => {
  // Level-up (Number Slam) is XP-driven; rank-up (Badge Forge) is coin-driven (specs/coin-based-ranks).
  useXpCelebrations();
  useCoinRankCelebrations();

  const current = useCelebrationStore((s) => s.current);
  const dismiss = useCelebrationStore((s) => s.dismiss);
  const isBlocked = useIsBlockedByOverlay();

  if (!current || isBlocked) return null;

  if (current.type === 'levelUp') {
    return (
      <LevelUpCelebration
        key={current.id}
        level={current.level}
        xpCurrent={current.xpCurrent}
        xpTarget={current.xpTarget}
        startProgress={current.startProgress}
        onDismiss={dismiss}
      />
    );
  }

  return (
    <RankUpCelebration
      key={current.id}
      fromRank={getRank(current.fromRank)}
      toRank={getRank(current.toRank)}
      level={current.level}
      xpCurrent={current.xpCurrent}
      xpTarget={current.xpTarget}
      share={current.share !== false}
      onDismiss={dismiss}
    />
  );
};
